import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { isDescriptorAvailable, sortUnitTypes } from './helpers';

/**
 * Determines whether or not a descriptor contains different variants of unitTypes, currently only using floor level for filtering variants
 * @param descriptor
 * @param selected
 * @returns
 */
export const unitTypeHasOptions = (descriptor: Descriptor, selected: UnitType) => {
    let result = false;
    for (const unitType of descriptor.unitTypes) {
        if (unitType.units.length != 0) {
            if (selected.floor != unitType.floor) {
                result = true;
            }
        }
    }
    return result;
};

/**
 * Uses a descriptor to find all the variants of unitTypes for displaying the unit options
 * Currently only supports floor level but can be expanded to filter by other keys also
 * @param descriptor
 * @returns
 */
export const unitOptionsTransformer = (descriptor: Descriptor): UnitType[] => {
    const options: UnitType[] = [];
    for (const unitType of descriptor.unitTypes) {
        if (unitType.units.length != 0) {
            const foundDriveUp = options.some(
                (el) => el.units[0]?.iEntryLoc === 1 && unitType.units[0]?.iEntryLoc === 1
            );
            if (!foundDriveUp) {
                options.push(unitType);
            }

            const foundSameFloor = options.some((el) => el.floor === unitType.floor);
            if (!foundSameFloor) {
                options.push(unitType);
            }
        }
    }

    const optionsSorted = options.sort((a, b) => {
        const aEntryLoc = a.units[0] && a.units[0].iEntryLoc === 1;
        const bEntryLoc = b.units[0] && b.units[0].iEntryLoc === 1;

        if (aEntryLoc && !bEntryLoc) {
            return -1;
        } else if (!aEntryLoc && bEntryLoc) {
            return 1;
        } else {
            return a.floor - b.floor;
        }
    });

    return optionsSorted;
};

/**
 * Determines the next biggest descriptor size in order to display an upsell
 * @param area
 * @param descriptors
 * @returns
 */
export const unitTypeNextSizeUp = (area: number, descriptors: Descriptor[]) => {
    const descriptorsSorted = [...descriptors].sort((a, b) => {
        return a.area - b.area;
    });
    for (const descriptor of descriptorsSorted) {
        if (descriptor.area > area && isDescriptorAvailable(descriptor)) {
            return descriptor;
        }
    }
    return null;
};

export interface UpgradeDescriptor extends Descriptor {
    upgradeReason: string;
    upgradeIcon: string;
    upgradeIconPrefix: string;
}

export const DescriptorOptions = (descriptor: Descriptor, descriptors: Descriptor[], location: Location) => {
    const descriptorMap = Object.fromEntries(descriptors.map((d) => [d._id, d]));
    const api = {
        isDescriptorUpsell: (): boolean => {
            return descriptor.upgradesTo && descriptor.upgradesTo.length > 0 && !!descriptor.upgradesTo.find((el) => descriptorMap[el._id]?.unitTypes?.find((u: UnitType) => u.hasComputedAvailable));
        },

        hasOptions: (): boolean => {
            if (api.isDescriptorUpsell()) {
                return true;
            } else {
                const sortedUnitType = sortUnitTypes(descriptor.unitTypes, location);

                const firstAvailableUnitType = sortedUnitType[0];
                if (!firstAvailableUnitType) {
                    return false;
                }

                return unitTypeHasOptions(descriptor, firstAvailableUnitType);
            }
        },

        getDescriptorUpsells: (descriptorList: Descriptor[]): UpgradeDescriptor[] => {
            const upsells: UpgradeDescriptor[] = [];
            for (const upgrade of descriptor.upgradesTo) {
                const found = descriptorList.find((el) => el._id === upgrade._id);
                if (found) {
                    upsells.push({ ...found, ...upgrade });
                }
            }
            return upsells;
        },

        getUnitTypeUpsells: (newDescriptor?: Descriptor): UnitType[] => {
            const unitTypes: UnitType[] = newDescriptor
                ? newDescriptor.unitTypes
                : descriptor.unitTypes;

            const options: UnitType[] = [];
            for (const unitType of unitTypes) {
                if (unitType.units.length != 0) {
                    const foundDriveUp = options.some(
                        (el) =>
                            el.units[0]?.iEntryLoc === 1 &&
                            unitType.units[0]?.iEntryLoc === 1
                    );
                    if (!foundDriveUp) {
                        options.push(unitType);
                    }

                    const foundSameFloor = options.some(
                        (el) => el.floor === unitType.floor
                    );
                    if (!foundSameFloor) {
                        options.push(unitType);
                    }
                }
            }

            const optionsSorted = options.sort((a, b) => {
                const aEntryLoc = a.units[0] && a.units[0].iEntryLoc === 1;
                const bEntryLoc = b.units[0] && b.units[0].iEntryLoc === 1;

                if (aEntryLoc && !bEntryLoc) {
                    return -1;
                } else if (!aEntryLoc && bEntryLoc) {
                    return 1;
                } else {
                    return a.floor - b.floor;
                }
            });

            return sortUnitTypes(optionsSorted, location);
        },

        getMatchingUnitNumbers: (unitType: UnitType): string[] => {
            const unitNumbers: string[] = [];
            for (const unit of unitType.units) {
                unitNumbers.push(`${unit.UnitID}`);
            }
            return unitNumbers;
        },

        nextSizeUp: (descriptors: Descriptor[], area: number) => {
            const descriptorsSorted = [...descriptors].sort((a, b) => {
                return a.area - b.area;
            });
            for (const descriptor of descriptorsSorted) {
                if (descriptor.area > area && isDescriptorAvailable(descriptor)) {
                    return descriptor;
                }
            }
            return null;
        },
    };

    return api;
};
